import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { dec, spalmaLetture } from '@praticheenergia/ad1-engine'
import type { Periodo } from '@praticheenergia/domain'
import {
  elencaContatoriConLetture,
  salvaLetture,
  mesiDelPeriodo,
  mesePrecedente,
  type ContatoreConLetture,
  type RigaLettura,
} from './letture.api'
import { interpretaIncollato } from './incolla'

// Riferimento stabile: un `?? []` inline creerebbe un array nuovo a ogni
// render finche' la query e' in caricamento, e l'useEffect qui sotto -- che
// dipende da `contatori` -- rientrerebbe in loop prima che la query si
// risolva (visto in pratica: OOM del processo di test).
const NESSUN_CONTATORE: ContatoreConLetture[] = []

interface Props {
  readonly impiantoId: number
  readonly anno: number
  readonly periodo: Periodo
}

/** Chiave della bozza locale: `${contatoreId}|${mese}`. I valori restano
 * stringhe per tutto il percorso — un `number` qui e' un errore fiscale. */
type Bozza = Record<string, string>

const chiave = (contatoreId: number, mese: number) => `${contatoreId}|${mese}`
/** Mese 0 nella bozza = lettura iniziale, che vive su `mesePrecedente`. */
const INIZIALE = 0

export function GrigliaLetture({ impiantoId, anno, periodo }: Props) {
  const queryClient = useQueryClient()
  const mesi = mesiDelPeriodo(periodo)
  const base = mesePrecedente(periodo, anno)
  const [bozza, setBozza] = useState<Bozza>({})
  const [finali, setFinali] = useState<Record<number, string>>({})
  const [errore, setErrore] = useState<string | null>(null)

  const { data: contatori = NESSUN_CONTATORE, isLoading } = useQuery({
    queryKey: ['contatori-letture', impiantoId, anno, periodo],
    queryFn: () => elencaContatoriConLetture(impiantoId, anno, periodo),
  })

  // La bozza parte dai valori salvati: si modifica cio' che c'e', non si riparte da vuoto.
  useEffect(() => {
    const iniziale: Bozza = {}
    for (const c of contatori) {
      for (const [mese, valore] of Object.entries(c.letture)) iniziale[chiave(c.id, Number(mese))] = valore
      if (c.iniziale !== null) iniziale[chiave(c.id, INIZIALE)] = c.iniziale
    }
    setBozza(iniziale)
  }, [contatori])

  function suSpalma(contatoreId: number, matricola: string) {
    setErrore(null)
    const da = bozza[chiave(contatoreId, INIZIALE)]
    const a = finali[contatoreId]
    if (!da || !a) {
      setErrore(`Inserisci lettura iniziale e valore finale per ${matricola}`)
      return
    }
    try {
      const passi = spalmaLetture(dec(da.replace(',', '.')), dec(a.replace(',', '.')), mesi, 0)
      setBozza((b) => {
        const prossima = { ...b }
        for (const p of passi) prossima[chiave(contatoreId, p.mese)] = p.valore.toString()
        return prossima
      })
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'errore nello spalmare le letture')
    }
  }

  /** Un incollato non valido non deve lasciare la griglia mezza riempita:
   * `interpretaIncollato` lancia prima di produrre qualunque riga. */
  function suIncolla(contatoreId: number, testo: string) {
    setErrore(null)
    try {
      const righe = interpretaIncollato(testo, mesi, contatoreId)
      setBozza((b) => {
        const prossima = { ...b }
        for (const r of righe) prossima[chiave(contatoreId, r.mese)] = r.valore
        return prossima
      })
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'incollato non interpretabile')
    }
  }

  async function suSalva() {
    setErrore(null)
    const righe: RigaLettura[] = []
    for (const c of contatori) {
      const iniziale = bozza[chiave(c.id, INIZIALE)]
      if (!c.kwhDiretti && iniziale !== undefined && iniziale.trim() !== '') {
        righe.push({ contatoreId: c.id, anno: base.anno, mese: base.mese, valore: iniziale })
      }
      for (const mese of mesi) {
        const valore = bozza[chiave(c.id, mese)]
        if (valore !== undefined && valore.trim() !== '') righe.push({ contatoreId: c.id, mese, valore })
      }
    }
    try {
      await salvaLetture(anno, righe)
      // Anche le dichiarazioni: il loro stato lo ricalcola il trigger (0032).
      await queryClient.invalidateQueries()
    } catch (err) {
      setErrore(err instanceof Error ? err.message : 'errore imprevisto')
    }
  }

  if (isLoading) return null
  if (contatori.length === 0) return <p>Nessun contatore per questo impianto</p>

  return (
    <Box>
      {errore !== null && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{errore}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Contatore</TableCell>
            <TableCell>Iniziale ({base.mese}/{base.anno})</TableCell>
            {mesi.map((m) => (
              <TableCell key={m}>{m}</TableCell>
            ))}
            <TableCell>Spalma</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contatori.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.matricola}</TableCell>
              <TableCell>
                {c.kwhDiretti ? (
                  '—'
                ) : (
                  <TextField
                    size="small"
                    label={`Contatore ${c.matricola}, lettura iniziale`}
                    value={bozza[chiave(c.id, INIZIALE)] ?? ''}
                    onChange={(e) => setBozza((b) => ({ ...b, [chiave(c.id, INIZIALE)]: e.target.value }))}
                  />
                )}
              </TableCell>
              {mesi.map((m) => (
                <TableCell key={m}>
                  <TextField
                    size="small"
                    label={`Contatore ${c.matricola}, mese ${m}`}
                    value={bozza[chiave(c.id, m)] ?? ''}
                    onChange={(e) => setBozza((b) => ({ ...b, [chiave(c.id, m)]: e.target.value }))}
                  />
                </TableCell>
              ))}
              <TableCell>
                <Stack direction="row" spacing={1}>
                  {/* Lo spalma ripartisce letture progressive: per i kWh diretti non ha senso. */}
                  {!c.kwhDiretti && (
                    <>
                      <TextField
                        size="small"
                        label={`Valore finale per ${c.matricola}`}
                        value={finali[c.id] ?? ''}
                        onChange={(e) => setFinali((s) => ({ ...s, [c.id]: e.target.value }))}
                      />
                      <Button onClick={() => suSpalma(c.id, c.matricola)}>Spalma su {c.matricola}</Button>
                    </>
                  )}
                  <TextField
                    size="small"
                    multiline
                    label={`Incolla una colonna per ${c.matricola}`}
                    value=""
                    onPaste={(e) => {
                      e.preventDefault()
                      suIncolla(c.id, e.clipboardData.getData('text'))
                    }}
                    onChange={() => {}}
                  />
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button variant="contained" sx={{ mt: 2 }} onClick={() => void suSalva()}>
        Salva letture
      </Button>
    </Box>
  )
}
